import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  BellRing,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleSlash2,
  Clock3,
  Loader2,
  Mail,
  MessageCircle,
  Phone,
  Plus,
  Search,
  Share2,
  Sparkles,
  Star,
  UserRoundCheck,
  X,
} from 'lucide-react';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import { showGlobalModal } from 'core/components/GlobalModal';
import { useDebounce } from 'core/hooks/useDebounce';
import httpclient from 'libs/Axios';
import { getApiErrorMessage } from 'features/DigitalMarketing/utils/apiResponse';
import { DigitalMarketingBreadcrumb } from '../../components/DigitalMarketingBreadcrumb';
import { DMButton, DMDiv, DMText } from '../../components/Primitives';
import './qualified-leads.css';

type LeadStatus = 'NEW' | 'CONVERTED' | 'CONTACTED' | 'NOT_INTERESTED';

type LeadRow = {
  id: number;
  parentName: string;
  studentName: string;
  joiningClass: string;
  currentSchool: string;
  address: string;
  source: string;
  phone: string;
  email: string;
  date: string;
  rawDate: string;
  status: LeadStatus;
};

type LeadForm = {
  parentName: string;
  studentName: string;
  gender: string;
  joiningClass: string;
  currentSchool: string;
  countryCode: string;
  phone: string;
  email: string;
  address: string;
  source: string;
};

type LeadsOverview = {
  all: number;
  hot: number;
  converted: number;
  distribution: Record<string, any>;
};

const LEADS_PATH = '/digital-marketing/leads';
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const STATUS_ORDER: LeadStatus[] = [
  'NEW',
  'CONVERTED',
  'CONTACTED',
  'NOT_INTERESTED',
];

// The product UI calls the first contacted stage "Contacted", while the
// admissions API represents that stage as IN_DEMO. Keep the UI vocabulary
// stable and translate only at the API boundary.
const API_STATUS_BY_UI: Record<LeadStatus, string> = {
  NEW: 'NEW',
  CONVERTED: 'CONVERTED',
  CONTACTED: 'IN_DEMO',
  NOT_INTERESTED: 'NOT_INTERESTED',
};

const STATUS_META: Record<
  LeadStatus,
  { label: string; icon: React.ComponentType<any>; color: string; tint: string }
> = {
  NEW: { label: 'New', icon: Star, color: '#4F46E5', tint: '#EEEAFB' },
  CONVERTED: {
    label: 'Converted',
    icon: CheckCircle2,
    color: '#16A34A',
    tint: '#E9F9F0',
  },
  CONTACTED: {
    label: 'Contacted',
    icon: Phone,
    color: '#E07A3C',
    tint: '#FFF2EA',
  },
  NOT_INTERESTED: {
    label: 'Not Interested',
    icon: CircleSlash2,
    color: '#EF4444',
    tint: '#FEECEC',
  },
};

const SOURCE_META = [
  { keys: ['instagram'], label: 'Instagram', color: '#3B82F6' },
  { keys: ['facebook', 'facebook_ads'], label: 'Facebook Ads', color: '#4F46E5' },
  { keys: ['parent_referrals', 'parent_referral', 'referrals'], label: 'Parent Referrals', color: '#F59E0B' },
  { keys: ['youtube'], label: 'Youtube', color: '#EF4444' },
  { keys: ['website', 'website_form'], label: 'Website Form', color: '#16A34A' },
  { keys: ['google_my_business', 'google'], label: 'Google My Business', color: '#EC4899' },
  { keys: ['others', 'other'], label: 'Others', color: '#9B9AB3' },
];

const SOURCE_OPTIONS = [
  'Social Media',
  'Instagram',
  'Facebook Ads',
  'Website Form',
  'Youtube',
  'Parent Referrals',
  'Google My Business',
  'Others',
];

const emptyForm: LeadForm = {
  parentName: '',
  studentName: '',
  gender: '',
  joiningClass: '',
  currentSchool: '',
  countryCode: '+91',
  phone: '',
  email: '',
  address: '',
  source: '',
};

const toTitleCase = (value: string) =>
  value
    .toLowerCase()
    .split(/[\s_]+/)
    .filter(Boolean)
    .map(part => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');

const normalizeKey = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const normalizeStatus = (value?: string): LeadStatus => {
  const status = String(value || '').trim().toUpperCase();
  if (status === 'IN_DEMO' || status === 'CONSIDERED' || status === 'CONTACTED') {
    return 'CONTACTED';
  }
  return STATUS_ORDER.includes(status as LeadStatus)
    ? (status as LeadStatus)
    : 'NEW';
};

const formatDate = (value?: string) => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString('en-GB');
};

const mapLead = (item: any, index: number): LeadRow => {
  const rawDate = String(
    item?.visited_at ||
      item?.VISITED_AT ||
      item?.created_at ||
      item?.CREATED_AT ||
      item?.date ||
      item?.DATE ||
      ''
  );
  return {
    id: Number(item?.lead_id || item?.LEAD_ID || item?.id || item?.ID || index + 1),
    parentName: item?.parent_name || item?.PARENT_NAME || item?.parentName || '—',
    studentName: item?.student_name || item?.STUDENT_NAME || item?.studentName || '—',
    joiningClass: String(item?.joining_class || item?.JOINING_CLASS || item?.joiningClass || '—'),
    currentSchool: item?.current_school || item?.CURRENT_SCHOOL || item?.currentSchool || '—',
    address: item?.address || item?.ADDRESS || '—',
    source: toTitleCase(String(item?.utm_source || item?.UTM_SOURCE || item?.source || item?.SOURCE || 'Others')),
    phone: String(item?.mobile_number || item?.MOBILE_NUMBER || item?.phone || item?.PHONE || '—'),
    email: item?.email_id || item?.EMAIL_ID || item?.email || item?.EMAIL || '—',
    date: formatDate(rawDate),
    rawDate,
    status: normalizeStatus(item?.lead_status || item?.LEAD_STATUS || item?.status || item?.STATUS),
  };
};

const getCount = (value: any) => {
  const resolved = typeof value === 'object' && value !== null ? value.count ?? value.COUNT : value;
  const number = Number(resolved);
  return Number.isFinite(number) ? number : 0;
};

const normalizeOverview = (data: any, rows: LeadRow[]): LeadsOverview => ({
  all: getCount(
    data?.all_leads ??
      data?.ALL_LEADS ??
      data?.all_leads_count ??
      data?.total_leads ??
      data?.TOTAL_LEADS ??
      rows.length
  ),
  hot: getCount(
    data?.hot_leads ??
      data?.HOT_LEADS ??
      data?.hot_leads_count ??
      rows.filter(row => row.status === 'NEW').length
  ),
  converted: getCount(
    data?.converted_leads ??
      data?.CONVERTED_LEADS ??
      data?.converted_leads_count ??
      rows.filter(row => row.status === 'CONVERTED').length
  ),
  distribution:
    data?.source_distribution ||
    data?.SOURCE_DISTRIBUTION ||
    data?.source_distribution_counts ||
    data?.sourceDistribution ||
    {},
});

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase() || 'L';

const avatarColors = ['#4F46E5', '#EC4899', '#16A34A', '#F59E0B', '#3B82F6', '#7C3AED'];

const getAgeInDays = (lead: LeadRow) => {
  let date = new Date(lead.rawDate);
  if (Number.isNaN(date.getTime()) && /^\d{2}\/\d{2}\/\d{4}$/.test(lead.date)) {
    const [day, month, year] = lead.date.split('/').map(Number);
    date = new Date(year, month - 1, day);
  }
  if (Number.isNaN(date.getTime())) return 0;
  return Math.max(0, Math.floor((Date.now() - date.getTime()) / 86400000));
};

const LeadAvatar = ({ lead, size = 'md' }: { lead: LeadRow; size?: 'sm' | 'md' | 'lg' }) => (
  <span
    className={`ql-avatar ql-avatar-${size}`}
    style={{ background: avatarColors[lead.id % avatarColors.length] }}
    aria-hidden="true"
  >
    {initials(lead.parentName)}
    <i style={{ background: getAgeInDays(lead) >= 2 ? '#EF4444' : '#22C55E' }} />
  </span>
);

const Header = ({
  title,
  subtitle,
  onBack,
  actions,
}: {
  title: string;
  subtitle: string;
  onBack?: () => void;
  actions?: React.ReactNode;
}) => (
  <DMDiv className="ql-header-stack">
    <DigitalMarketingBreadcrumb items={[{ label: title }]} />
    <DMDiv className="ql-header">
      <DMDiv className="ql-title-wrap">
      {onBack && (
        <DMButton type="button" className="ql-icon-button" onClick={onBack} aria-label="Back">
          <ArrowLeft />
        </DMButton>
      )}
      <DMDiv>
        <DMText as="h1">{title}</DMText>
        <DMText>{subtitle}</DMText>
      </DMDiv>
      </DMDiv>
      {actions && <DMDiv className="ql-header-actions">{actions}</DMDiv>}
    </DMDiv>
  </DMDiv>
);

const Modal = ({
  title,
  description,
  onClose,
  children,
  className = '',
}: {
  title: string;
  description?: string;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
}) => (
  <DMDiv className="ql-modal-layer" role="presentation" onMouseDown={event => event.target === event.currentTarget && onClose()}>
    <DMDiv role="dialog" aria-modal="true" aria-labelledby="ql-modal-title" className={`ql-modal ${className}`}>
      <DMDiv className="ql-modal-header">
        <DMDiv>
          <DMText as="h2" id="ql-modal-title">{title}</DMText>
          {description && <DMText>{description}</DMText>}
        </DMDiv>
        <DMButton type="button" onClick={onClose} aria-label="Close dialog"><X /></DMButton>
      </DMDiv>
      {children}
    </DMDiv>
  </DMDiv>
);

export function LeadsManagement() {
  const authData = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isCreateView = location.pathname.endsWith('/create');
  const isListView = location.pathname.endsWith('/list');

  const [rows, setRows] = React.useState<LeadRow[]>([]);
  const [overview, setOverview] = React.useState<LeadsOverview>({ all: 0, hot: 0, converted: 0, distribution: {} });
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [updatingId, setUpdatingId] = React.useState<number | null>(null);
  const [movingLeadId, setMovingLeadId] = React.useState<number | null>(null);
  const [dragToast, setDragToast] = React.useState('');
  const [search, setSearch] = React.useState('');
  const deferredSearch = useDebounce(search, 250);
  const [classFilter, setClassFilter] = React.useState('ALL');
  const [sourceFilter, setSourceFilter] = React.useState('ALL');
  const [dateFilter, setDateFilter] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<'ALL' | LeadStatus | 'FOLLOW_UP'>('ALL');
  const [pageIndex, setPageIndex] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
  const [detailLead, setDetailLead] = React.useState<LeadRow | null>(null);
  const [detailNote, setDetailNote] = React.useState('');
  const [smsLead, setSmsLead] = React.useState<LeadRow | null>(null);
  const [smsText, setSmsText] = React.useState('');
  const [showAiModal, setShowAiModal] = React.useState(false);
  const [showCustomSource, setShowCustomSource] = React.useState(false);
  const [customSource, setCustomSource] = React.useState('');
  const [form, setForm] = React.useState<LeadForm>(emptyForm);

  const fetchOverview = React.useCallback(async () => {
    if (!authData?.BRANCH_ID || !authData?.INSTITUTE_ID) return;
    try {
      const query = new URLSearchParams({
        branch_id: String(authData.BRANCH_ID),
        institute_id: String(authData.INSTITUTE_ID),
      });
      const response = await httpclient().get(`/master/viewleads?${query.toString()}`);
      setOverview(current => normalizeOverview(response?.data?.data || {}, rows.length ? rows : []));
    } catch (error: any) {
      showGlobalModal(getApiErrorMessage(error, 'Unable to fetch leads dashboard'), 'error');
    }
  }, [authData?.BRANCH_ID, authData?.INSTITUTE_ID, rows]);

  const fetchRows = React.useCallback(async () => {
    if (!authData?.BRANCH_ID || !authData?.INSTITUTE_ID) return;
    setIsLoading(true);
    try {
      const response = await httpclient().get('/master/leads/list?page_index=1&page_size=50');
      const data = response?.data?.data || {};
      const leads = data.leads || data.Leads || data.lead_list || data.list || [];
      const nextRows = leads.map(mapLead);
      setRows(nextRows);
      setOverview(current => (current.all ? current : normalizeOverview({}, nextRows)));
    } catch (error: any) {
      setRows([]);
      showGlobalModal(getApiErrorMessage(error, 'Unable to fetch leads'), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [authData?.BRANCH_ID, authData?.INSTITUTE_ID]);

  React.useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  React.useEffect(() => {
    fetchOverview();
    // The overview endpoint is independent of the currently rendered rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData?.BRANCH_ID, authData?.INSTITUTE_ID]);

  React.useEffect(() => {
    setPageIndex(1);
    setSelectedIds([]);
  }, [deferredSearch, sourceFilter, classFilter, dateFilter, statusFilter]);

  const filteredRows = React.useMemo(() => {
    const term = deferredSearch.trim().toLowerCase();
    return rows.filter(lead => {
      const matchesSearch =
        !term ||
        [lead.parentName, lead.studentName, lead.email, lead.phone, lead.source].some(value =>
          value.toLowerCase().includes(term)
        );
      const matchesClass = classFilter === 'ALL' || lead.joiningClass === classFilter;
      const matchesSource = sourceFilter === 'ALL' || normalizeKey(lead.source) === normalizeKey(sourceFilter);
      const matchesDate = !dateFilter || lead.rawDate.slice(0, 10) === dateFilter || lead.date === formatDate(dateFilter);
      const needsFollowUp = ['NEW', 'CONTACTED'].includes(lead.status) && getAgeInDays(lead) >= 2;
      const matchesStatus =
        statusFilter === 'ALL' ||
        (statusFilter === 'FOLLOW_UP' ? needsFollowUp : lead.status === statusFilter);
      return matchesSearch && matchesClass && matchesSource && matchesDate && matchesStatus;
    });
  }, [rows, deferredSearch, classFilter, sourceFilter, dateFilter, statusFilter]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const currentPage = Math.min(pageIndex, pageCount);
  const visibleListRows = filteredRows.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const classes = React.useMemo(
    () => Array.from(new Set(rows.map(row => row.joiningClass).filter(value => value !== '—'))).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    [rows]
  );

  const sourceRows = React.useMemo(() => {
    const normalizedDistribution = Object.entries(overview.distribution || {}).reduce<Record<string, number>>(
      (result, [key, value]) => ({ ...result, [normalizeKey(key)]: getCount(value) }),
      {}
    );
    const knownKeys = new Set(SOURCE_META.filter(source => source.label !== 'Others').flatMap(source => source.keys));
    const values = SOURCE_META.map(source => {
      let count = 0;
      if (source.label === 'Others') {
        count = Object.entries(normalizedDistribution).reduce((sum, [key, value]) => sum + (knownKeys.has(key) ? 0 : value), 0);
      } else {
        count = source.keys.reduce((sum, key) => sum + (normalizedDistribution[key] || 0), 0);
      }
      if (!count) {
        count = rows.filter(row =>
          source.label === 'Others'
            ? !SOURCE_META.slice(0, -1).some(item => item.keys.includes(normalizeKey(row.source)))
            : source.keys.includes(normalizeKey(row.source))
        ).length;
      }
      return { ...source, count };
    });
    const max = Math.max(1, ...values.map(value => value.count));
    return values.map(value => ({ ...value, width: `${Math.max(value.count ? 12 : 0, (value.count / max) * 100)}%` }));
  }, [overview.distribution, rows]);

  const conversionRate = overview.all ? Math.round((overview.converted / overview.all) * 100) : 0;
  const followUps = React.useMemo(
    () => rows.filter(row => ['NEW', 'CONTACTED'].includes(row.status) && getAgeInDays(row) >= 2).sort((a, b) => getAgeInDays(b) - getAgeInDays(a)),
    [rows]
  );

  const updateStatus = React.useCallback(
    async (lead: LeadRow, nextStatus: LeadStatus, feedback: 'modal' | 'toast' = 'modal') => {
      if (lead.status === nextStatus) return true;
      const previousStatus = lead.status;
      setUpdatingId(lead.id);
      setMovingLeadId(lead.id);
      setRows(current => current.map(row => (row.id === lead.id ? { ...row, status: nextStatus } : row)));
      try {
        await httpclient().post(`/master/editLeadStatus/${lead.id}`, {
          lead_status: API_STATUS_BY_UI[nextStatus],
        });
        if (feedback === 'toast') {
          setDragToast('Lead moved successfully');
          window.setTimeout(() => setDragToast(''), 2400);
        } else {
          showGlobalModal('Lead status updated successfully');
        }
        fetchOverview();
        return true;
      } catch (error: any) {
        setRows(current => current.map(row => (row.id === lead.id ? { ...row, status: previousStatus } : row)));
        showGlobalModal(getApiErrorMessage(error, 'Unable to update lead status'), 'error');
        return false;
      } finally {
        setUpdatingId(null);
        setMovingLeadId(null);
      }
    },
    [fetchOverview]
  );

  const handleBulkContacted = async () => {
    const selected = rows.filter(row => selectedIds.includes(row.id));
    for (const lead of selected) {
      // Sequential updates avoid racing the status endpoint and keep rollback predictable.
      // eslint-disable-next-line no-await-in-loop
      const succeeded = await updateStatus(lead, 'CONTACTED', 'modal');
      if (!succeeded) return;
    }
    setSelectedIds([]);
    showGlobalModal(`${selected.length} lead${selected.length === 1 ? '' : 's'} marked contacted`);
  };

  const handleExport = () => {
    if (!filteredRows.length) {
      showGlobalModal('No leads available to export', 'warning');
      return;
    }
    const XLSX = require('xlsx-js-style');
    const sheet = XLSX.utils.json_to_sheet(
      filteredRows.map(row => ({
        'Parent Name': row.parentName,
        'Student Name': row.studentName,
        'Joining Class': row.joiningClass,
        Source: row.source,
        Phone: row.phone,
        Email: row.email,
        Date: row.date,
        Status: STATUS_META[row.status].label,
      }))
    );
    sheet['!cols'] = [24, 24, 16, 20, 18, 30, 14, 18].map(wch => ({ wch }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Leads');
    XLSX.writeFile(workbook, `digital-marketing-leads-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const formErrors = React.useMemo(() => {
    const letters = /^[A-Za-z\s'.-]+$/;
    const email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return {
      parentName: form.parentName && !letters.test(form.parentName) ? 'Use letters only' : '',
      studentName: form.studentName && !letters.test(form.studentName) ? 'Use letters only' : '',
      currentSchool: form.currentSchool && !letters.test(form.currentSchool) ? 'Use letters only' : '',
      joiningClass: form.joiningClass && !/^\d+$/.test(form.joiningClass) ? 'Enter a class number' : '',
      phone: form.phone && form.phone.replace(/\D/g, '').length !== 10 ? 'Enter exactly 10 digits' : '',
      email: form.email && !email.test(form.email) ? 'Enter a valid email address' : '',
    };
  }, [form]);

  const requiredFormKeys: Array<keyof LeadForm> = [
    'parentName', 'studentName', 'gender', 'joiningClass', 'currentSchool', 'phone', 'email', 'address', 'source',
  ];
  const completedFields = requiredFormKeys.filter(key => Boolean(form[key].trim()) && !formErrors[key as keyof typeof formErrors]).length;
  const formComplete = completedFields === requiredFormKeys.length && Object.values(formErrors).every(error => !error);

  const setFormValue = (key: keyof LeadForm, value: string) => setForm(current => ({ ...current, [key]: value }));

  const submitLead = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!formComplete) {
      showGlobalModal('Complete all required fields before saving lead', 'warning');
      return;
    }
    setIsSaving(true);
    try {
      await httpclient().post('/master/upsertLead', {
        student_name: form.studentName.trim(),
        parent_name: form.parentName.trim(),
        student_gender: form.gender,
        joining_class: form.joiningClass.trim(),
        current_school: form.currentSchool.trim(),
        address: form.address.trim(),
        email_id: form.email.trim(),
        mobile_number: `${form.countryCode} ${form.phone.trim()}`,
        utm_source: form.source,
      });
      setForm(emptyForm);
      await Promise.all([fetchRows(), fetchOverview()]);
      showGlobalModal('Lead saved successfully');
      navigate(LEADS_PATH);
    } catch (error: any) {
      showGlobalModal(getApiErrorMessage(error, 'Unable to save lead'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const sourceIcon = (source: string) => {
    const meta = SOURCE_META.find(item => item.keys.includes(normalizeKey(source))) || SOURCE_META[SOURCE_META.length - 1];
    return meta;
  };

  const renderLeadCard = (lead: LeadRow) => {
    const source = sourceIcon(lead.source);
    if (movingLeadId === lead.id) {
      return <DMDiv key={lead.id} as="article" className="ql-lead-card ql-lead-card-loading" aria-busy="true">
        <DMDiv className="ql-lead-card-main"><DMDiv className="ql-skeleton-line ql-skeleton-avatar" /><DMDiv className="ql-skeleton-copy"><DMDiv className="ql-skeleton-line ql-skeleton-name" /><DMDiv className="ql-skeleton-line ql-skeleton-subtitle" /></DMDiv></DMDiv>
        <DMDiv className="ql-skeleton-line ql-skeleton-meta" /><DMDiv className="ql-skeleton-actions"><DMDiv className="ql-skeleton-line" /><DMDiv className="ql-skeleton-line" /><DMDiv className="ql-skeleton-line" /></DMDiv>
      </DMDiv>;
    }
    return (
      <DMDiv
        as="article"
        key={lead.id}
        className="ql-lead-card"
        draggable
        onDragStart={event => event.dataTransfer.setData('text/lead-id', String(lead.id))}
        onClick={() => setDetailLead(lead)}
      >
        <DMDiv className="ql-lead-card-main">
          <LeadAvatar lead={lead} />
          <DMDiv className="ql-lead-copy">
            <DMText as="strong">{lead.parentName}</DMText>
            <DMText>{lead.studentName} · Class {lead.joiningClass}</DMText>
          </DMDiv>
        </DMDiv>
        <DMDiv className="ql-lead-meta">
          <DMText as="span" className="ql-source-pill" style={{ color: source.color }}>{lead.source}</DMText>
          <DMText as="span" className="ql-lead-date">
            {lead.date}
          </DMText>
        </DMDiv>
        <DMDiv className="ql-lead-actions" onClick={event => event.stopPropagation()}>
          <a href={`tel:${lead.phone.replace(/\s+/g, '')}`}><Phone /> Call</a>
          <a href={`https://wa.me/${lead.phone.replace(/[^\d]/g, '')}`} target="_blank" rel="noreferrer"><MessageCircle /> WhatsApp</a>
          <DMButton type="button" onClick={() => { setSmsLead(lead); setSmsText(''); }}><MessageCircle /> SMS</DMButton>
        </DMDiv>
      </DMDiv>
    );
  };

  const filters = (
    <DMDiv className="ql-filters">
      <label className="ql-search"><Search /><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search leads" aria-label="Search leads" /></label>
      <label className="ql-select"><select value={classFilter} onChange={event => setClassFilter(event.target.value)} aria-label="Filter by class"><option value="ALL">All Classes</option>{classes.map(value => <option key={value} value={value}>Class {value}</option>)}</select><ChevronDown /></label>
      <label className="ql-select"><Share2 /><select value={sourceFilter} onChange={event => setSourceFilter(event.target.value)} aria-label="Filter by source"><option value="ALL">All Sources</option>{SOURCE_OPTIONS.filter(value => value !== 'Social Media').map(value => <option key={value}>{value}</option>)}</select><ChevronDown /></label>
      <label className="ql-date" onClick={event => { const input = event.currentTarget.querySelector('input'); if (input && event.target !== input) { input.focus(); (input as HTMLInputElement & { showPicker?: () => void }).showPicker?.(); } }}><input type="date" value={dateFilter} onChange={event => setDateFilter(event.target.value)} aria-label="Filter by date" /><CalendarDays aria-hidden="true" /></label>
      {(search || classFilter !== 'ALL' || sourceFilter !== 'ALL' || dateFilter) && <DMButton type="button" className="ql-clear" onClick={() => { setSearch(''); setClassFilter('ALL'); setSourceFilter('ALL'); setDateFilter(''); }}>Clear</DMButton>}
    </DMDiv>
  );

  if (isCreateView) {
    const checklist = [
      ['Parent name', 'parentName'], ['Student name', 'studentName'], ['Gender', 'gender'], ['Joining class', 'joiningClass'],
      ['Current school', 'currentSchool'], ['Phone number', 'phone'], ['Email address', 'email'], ['Address', 'address'], ['Lead source', 'source'],
    ] as Array<[string, keyof LeadForm]>;
    return (
      <DMDiv as="main" className="qualified-leads ql-create-page">
        <Header title="Create New Lead" subtitle="Every lead is automatically sent to the admission counsellor mobile app" onBack={() => navigate(LEADS_PATH)} />
        <DMDiv className="ql-create-layout">
          <DMDiv as="form" className="ql-create-form" onSubmit={submitLead} noValidate>
            <section>
              <DMDiv className="ql-section-title"><span>01</span><DMText as="h2">Student &amp; Guardian</DMText><DMText>— who are we speaking with?</DMText></DMDiv>
              <DMDiv className="ql-form-grid ql-form-grid-2">
                <FormField label="Parent name" required value={form.parentName} error={formErrors.parentName} onChange={value => setFormValue('parentName', value)} placeholder="Parent name" />
                <FormField label="Student name" required value={form.studentName} error={formErrors.studentName} onChange={value => setFormValue('studentName', value)} placeholder="Student name" />
              </DMDiv>
              <DMDiv className="ql-form-grid ql-student-row">
                <fieldset className="ql-gender"><legend>Gender <b>*</b></legend><DMDiv>{['Male', 'Female'].map(gender => <DMButton type="button" key={gender} className={form.gender === gender ? 'active' : ''} onClick={() => setFormValue('gender', gender)}>{gender}</DMButton>)}</DMDiv></fieldset>
                <FormField label="Joining class" required value={form.joiningClass} error={formErrors.joiningClass} onChange={value => setFormValue('joiningClass', value.replace(/\D/g, '').slice(0, 2))} placeholder="e.g. 3" inputMode="numeric" />
                <FormField label="Current school" required value={form.currentSchool} error={formErrors.currentSchool} onChange={value => setFormValue('currentSchool', value)} placeholder="Current school" />
              </DMDiv>
            </section>
            <section>
              <DMDiv className="ql-section-title"><span>02</span><DMText as="h2">Contact Details</DMText><DMText>— how do we reach the parent?</DMText></DMDiv>
              <DMDiv className="ql-form-grid ql-form-grid-2">
                <label className="ql-field"><span>Phone number <b>*</b></span><DMDiv className="ql-phone-field"><select value={form.countryCode} onChange={event => setFormValue('countryCode', event.target.value)} aria-label="Country code"><option>+91</option><option>+1</option><option>+44</option><option>+971</option><option>+65</option><option>+61</option></select><input value={form.phone} onChange={event => setFormValue('phone', event.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit mobile number" inputMode="numeric" /></DMDiv>{formErrors.phone && <small>{formErrors.phone}</small>}</label>
                <FormField label="Email address" required type="email" value={form.email} error={formErrors.email} onChange={value => setFormValue('email', value)} placeholder="parent@example.com" />
              </DMDiv>
              <DMDiv className="ql-form-grid ql-form-grid-1"><FormField label="Address" required value={form.address} onChange={value => setFormValue('address', value)} placeholder="Residential address" /></DMDiv>
            </section>
            <section>
              <DMDiv className="ql-section-title"><span>03</span><DMText as="h2">Lead Source</DMText><DMText>— where did they hear about the school?</DMText></DMDiv>
              <DMDiv className="ql-source-options">{SOURCE_OPTIONS.map(source => {
                const active = form.source === source || (source === 'Others' && form.source && !SOURCE_OPTIONS.includes(form.source));
                const meta = sourceIcon(source);
                return <DMButton type="button" key={source} className={active ? 'active' : ''} style={active ? { borderColor: meta.color, color: meta.color, background: `${meta.color}12` } : undefined} onClick={() => source === 'Others' ? setShowCustomSource(true) : setFormValue('source', source)}><Share2 />{source}</DMButton>;
              })}</DMDiv>
            </section>
            <DMDiv className="ql-form-actions"><DMButton type="button" className="ql-secondary" onClick={() => navigate(LEADS_PATH)}>Cancel</DMButton><DMButton type="submit" className="ql-primary" disabled={!formComplete || isSaving}>{isSaving ? <><Loader2 className="ql-spin" /> Saving…</> : <><Check /> Save Lead</>}</DMButton></DMDiv>
          </DMDiv>
          <aside className="ql-preview-panel">
            <DMDiv className="ql-progress-ring" style={{ '--progress': `${(completedFields / requiredFormKeys.length) * 360}deg` } as React.CSSProperties}><span>{Math.round((completedFields / requiredFormKeys.length) * 100)}%</span></DMDiv>
            <DMText as="h2">Lead completeness</DMText><DMText>{completedFields} of {requiredFormKeys.length} required details added</DMText>
            <DMDiv className="ql-preview-person"><span style={{ background: avatarColors[Math.max(1, form.parentName.length) % avatarColors.length] }}>{initials(form.parentName || 'New Parent')}</span><DMDiv><strong>{form.parentName || 'New Parent'}</strong><small>{form.studentName || 'Student name pending'}</small></DMDiv></DMDiv>
            <DMDiv className="ql-checklist">{checklist.map(([label, key]) => { const done = Boolean(form[key].trim()) && !formErrors[key as keyof typeof formErrors]; return <DMDiv key={key} className={done ? 'done' : ''}><span>{done && <Check />}</span>{label}</DMDiv>; })}</DMDiv>
          </aside>
        </DMDiv>
        {showCustomSource && <Modal title="Specify Lead Source" description="Tell us where this lead actually came from" onClose={() => setShowCustomSource(false)}><label className="ql-field ql-modal-field"><span>Lead source</span><input autoFocus value={customSource} onChange={event => setCustomSource(event.target.value)} placeholder="e.g. Newspaper ad, walk-in, alumni" /></label><DMDiv className="ql-modal-actions"><DMButton type="button" className="ql-secondary" onClick={() => setShowCustomSource(false)}>Cancel</DMButton><DMButton type="button" className="ql-primary" disabled={!customSource.trim()} onClick={() => { setFormValue('source', customSource.trim()); setShowCustomSource(false); setCustomSource(''); }}>Save</DMButton></DMDiv></Modal>}
      </DMDiv>
    );
  }

  if (isListView) {
    const filtersWithStatus = (
      <DMDiv className="ql-list-toolbar">
        {filters}
        <label className="ql-status-chips">
          <select value={statusFilter} onChange={event => setStatusFilter(event.target.value as typeof statusFilter)} aria-label="Filter leads by status">
            {(['ALL', ...STATUS_ORDER, 'FOLLOW_UP'] as const).map(status => {
              const label = status === 'ALL' ? 'All Leads' : status === 'FOLLOW_UP' ? 'Needs Follow-up' : STATUS_META[status].label;
              return <option key={status} value={status}>{label}</option>;
            })}
          </select>
          <ChevronDown aria-hidden="true" />
        </label>
      </DMDiv>
    );
    const allSelected = visibleListRows.length > 0 && visibleListRows.every(row => selectedIds.includes(row.id));
    return (
      <DMDiv as="main" className="qualified-leads ql-list-page">
        <Header title="Leads List" subtitle="Search, filter, export and manage every admission lead in one place" onBack={() => navigate(LEADS_PATH)} actions={<><DMButton type="button" className="ql-primary" onClick={() => navigate(`${LEADS_PATH}/create`)}><Plus /> Add New Lead</DMButton></>} />
        {filtersWithStatus}
        {selectedIds.length > 0 && <DMDiv className="ql-bulk-bar"><strong>{selectedIds.length} selected</strong><span /><DMButton type="button" disabled={updatingId !== null} onClick={handleBulkContacted}>Mark Contacted</DMButton><DMButton type="button" onClick={() => setSelectedIds([])}>Clear</DMButton></DMDiv>}
        <DMDiv className="ql-table-card">
          <DMDiv className="ql-table-scroll">
            <table><thead><tr><th><input type="checkbox" checked={allSelected} onChange={() => setSelectedIds(current => allSelected ? current.filter(id => !visibleListRows.some(row => row.id === id)) : Array.from(new Set([...current, ...visibleListRows.map(row => row.id)])))} aria-label="Select all leads on page" /></th><th>Lead</th><th>Class</th><th>Source</th><th>Phone</th><th>Email</th><th>Date</th><th>Status</th><th /></tr></thead>
              <tbody>{isLoading ? <tr><td colSpan={9} className="ql-table-state"><Loader2 className="ql-spin" /> Loading leads…</td></tr> : visibleListRows.length ? visibleListRows.map(lead => { const meta = STATUS_META[lead.status]; return <tr key={lead.id}><td><input type="checkbox" checked={selectedIds.includes(lead.id)} onChange={() => setSelectedIds(current => current.includes(lead.id) ? current.filter(id => id !== lead.id) : [...current, lead.id])} aria-label={`Select ${lead.parentName}`} /></td><td><DMButton type="button" className="ql-table-lead" onClick={() => setDetailLead(lead)}><LeadAvatar lead={lead} size="sm" /><span><strong>{lead.parentName}</strong><small>{lead.studentName}</small></span></DMButton></td><td>{lead.joiningClass}</td><td>{lead.source}</td><td><a href={`tel:${lead.phone.replace(/\s+/g, '')}`}>{lead.phone}</a></td><td><a href={`mailto:${lead.email}`}>{lead.email}</a></td><td>{lead.date}</td><td><label className="ql-status-select" style={{ color: meta.color, background: meta.tint }}><select value={lead.status} disabled={updatingId === lead.id} onChange={event => updateStatus(lead, event.target.value as LeadStatus)}>{STATUS_ORDER.map(status => <option key={status} value={status}>{STATUS_META[status].label}</option>)}</select><ChevronDown /></label></td><td><DMButton type="button" className="ql-row-open" onClick={() => setDetailLead(lead)} aria-label={`View ${lead.parentName}`}><ChevronRight /></DMButton></td></tr>; }) : <tr><td colSpan={9} className="ql-table-state">No leads match these filters.</td></tr>}</tbody></table>
          </DMDiv>
          <DMDiv className="ql-pagination"><DMText>{filteredRows.length ? `Showing ${(currentPage - 1) * pageSize + 1} to ${Math.min(currentPage * pageSize, filteredRows.length)} of ${filteredRows.length} leads` : 'No leads found'}</DMText><span /><label><select value={pageSize} onChange={event => setPageSize(Number(event.target.value))}>{PAGE_SIZE_OPTIONS.map(size => <option key={size} value={size}>{size} / page</option>)}</select><ChevronDown /></label><DMButton type="button" disabled={currentPage === 1} onClick={() => setPageIndex(page => Math.max(1, page - 1))}><ChevronLeft /></DMButton>{Array.from({ length: pageCount }).slice(0, 7).map((_, index) => <DMButton type="button" key={index + 1} className={currentPage === index + 1 ? 'active' : ''} onClick={() => setPageIndex(index + 1)}>{index + 1}</DMButton>)}<DMButton type="button" disabled={currentPage === pageCount} onClick={() => setPageIndex(page => Math.min(pageCount, page + 1))}><ChevronRight /></DMButton></DMDiv>
        </DMDiv>
        {detailLead && <LeadDetail lead={detailLead} note={detailNote} setNote={setDetailNote} onClose={() => setDetailLead(null)} onStatusChange={status => { updateStatus(detailLead, status); setDetailLead(current => current ? { ...current, status } : current); }} onSms={() => { setSmsLead(detailLead); setSmsText(''); }} />}
        {smsLead && <SmsModal lead={smsLead} text={smsText} setText={setSmsText} onClose={() => setSmsLead(null)} />}
      </DMDiv>
    );
  }

  return (
    <DMDiv as="main" className="qualified-leads ql-board-page">
      <Header title="Leads Management" subtitle="Every lead is automatically sent to the admission counsellor mobile app" actions={<><DMButton type="button" className="ql-ai-button" onClick={() => setShowAiModal(true)}><MessageCircle /> Setup Conversational AI <span>NEW</span></DMButton><DMButton type="button" className="ql-primary" onClick={() => navigate(`${LEADS_PATH}/create`)}><Plus /> Add New Lead</DMButton></>} />
      <DMDiv className="ql-summary-grid">
        <SummaryCard label="Total Admissions" value={overview.converted} helper="Live vs last month" icon={UserRoundCheck} color="#16A34A" tint="#E9F9F0" loading={isLoading} />
        <SummaryCard label="Hot Leads" value={overview.hot} helper="Live vs last month" icon={Clock3} color="#E07A3C" tint="#FFF2EA" loading={isLoading} />
        <SummaryCard label="Conversion Rate" value={`${conversionRate}%`} helper="Leads turned into admissions" icon={BarChart3} color="#3B82F6" tint="#EAF2FF" loading={isLoading} />
      </DMDiv>
      <DMDiv className="ql-board-toolbar">{filters}<DMButton type="button" className="ql-view-list" onClick={() => navigate(`${LEADS_PATH}/list`)}>View as list <ChevronRight /></DMButton></DMDiv>
      <DMDiv className="ql-board-layout">
        <DMDiv className="ql-kanban">
          {STATUS_ORDER.map(status => {
            const meta = STATUS_META[status];
            const Icon = meta.icon;
            const statusRows = filteredRows.filter(row => row.status === status);
            return <section key={status} className="ql-column" onDragOver={event => event.preventDefault()} onDrop={event => { const id = Number(event.dataTransfer.getData('text/lead-id')); const lead = rows.find(row => row.id === id); if (lead) updateStatus(lead, status, 'toast'); }}><DMDiv className="ql-column-header"><span style={{ background: meta.tint, color: meta.color }}><Icon /></span><DMText as="h2">{meta.label}</DMText><b style={{ background: meta.tint, color: meta.color }}>{statusRows.length}</b></DMDiv><DMDiv className="ql-column-body">{isLoading ? <DMDiv className="ql-card-skeleton" /> : statusRows.length ? statusRows.map(renderLeadCard) : <DMDiv className="ql-empty-column">Drop leads here</DMDiv>}</DMDiv></section>;
          })}
        </DMDiv>
        <aside className="ql-board-aside">
          <section className="ql-followups"><DMDiv className="ql-side-title"><BellRing /><DMText as="h2">Needs Follow-up</DMText></DMDiv>{followUps.slice(0, 3).map(lead => <DMButton type="button" key={lead.id} className="ql-followup-row" onClick={() => setDetailLead(lead)}><LeadAvatar lead={lead} size="sm" /><strong>{lead.parentName}</strong><span>{getAgeInDays(lead)}d</span></DMButton>)}{!followUps.length && <DMText className="ql-side-empty">No overdue follow-ups</DMText>}{followUps.length > 3 && <DMButton type="button" className="ql-side-link" onClick={() => { setStatusFilter('FOLLOW_UP'); navigate(`${LEADS_PATH}/list`); }}>View all {followUps.length} <ArrowRight /></DMButton>}<DMText className="ql-side-note">Leads idle 2+ days without contact</DMText></section>
          <section className="ql-source-chart"><DMText as="h2">Leads by Source</DMText>{sourceRows.map(source => <DMDiv key={source.label} className="ql-source-row"><DMDiv><span>{source.label}</span><b>{source.count}</b></DMDiv><i><span style={{ width: source.width, background: source.color }} /></i></DMDiv>)}</section>
        </aside>
      </DMDiv>
      {detailLead && <LeadDetail lead={detailLead} note={detailNote} setNote={setDetailNote} onClose={() => setDetailLead(null)} onStatusChange={status => { updateStatus(detailLead, status); setDetailLead(current => current ? { ...current, status } : current); }} onSms={() => { setSmsLead(detailLead); setSmsText(''); }} />}
      {smsLead && <SmsModal lead={smsLead} text={smsText} setText={setSmsText} onClose={() => setSmsLead(null)} />}
      {showAiModal && <Modal title="Coming Soon" onClose={() => setShowAiModal(false)} className="ql-ai-modal"><DMDiv className="ql-ai-orb"><MessageCircle /></DMDiv><DMText>Conversational AI for leads is on its way — it will help auto-engage and qualify leads over chat.</DMText><DMButton type="button" className="ql-primary" onClick={() => setShowAiModal(false)}>Got it</DMButton></Modal>}
      {dragToast && <DMDiv className="ql-drag-toast" role="status" aria-live="polite"><Check />{dragToast}</DMDiv>}
    </DMDiv>
  );
}

function SummaryCard({ label, value, helper, icon: Icon, color, tint, loading }: { label: string; value: React.ReactNode; helper: string; icon: React.ComponentType<any>; color: string; tint: string; loading: boolean }) {
  return <section className={`ql-summary-card ${loading ? 'loading' : ''}`}><DMDiv><DMText>{label}</DMText><strong>{loading ? '—' : value}</strong><small>{helper}</small></DMDiv><span style={{ color, background: tint }}><Icon /></span></section>;
}

function FormField({ label, required, value, error, onChange, placeholder, type = 'text', inputMode }: { label: string; required?: boolean; value: string; error?: string; onChange: (value: string) => void; placeholder: string; type?: string; inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'] }) {
  return <label className="ql-field"><span>{label} {required && <b>*</b>}{value && !error && <Check />}</span><input type={type} inputMode={inputMode} value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} aria-invalid={Boolean(error)} />{error && <small>{error}</small>}</label>;
}

function SmsModal({ lead, text, setText, onClose }: { lead: LeadRow; text: string; setText: (value: string) => void; onClose: () => void }) {
  const send = () => { window.location.href = `sms:${lead.phone.replace(/\s+/g, '')}?body=${encodeURIComponent(text)}`; onClose(); };
  return <Modal title="Send SMS" description={`Please enter your message to ${lead.parentName}`} onClose={onClose}><label className="ql-field ql-modal-field"><span>Message</span><textarea autoFocus value={text} onChange={event => setText(event.target.value)} placeholder="Type your message…" /></label><DMDiv className="ql-modal-actions"><DMButton type="button" className="ql-secondary" onClick={onClose}>Cancel</DMButton><DMButton type="button" className="ql-primary" disabled={!text.trim()} onClick={send}>Send</DMButton></DMDiv></Modal>;
}

function LeadDetail({ lead, note, setNote, onClose, onStatusChange, onSms }: { lead: LeadRow; note: string; setNote: (value: string) => void; onClose: () => void; onStatusChange: (status: LeadStatus) => void; onSms: () => void }) {
  return <DMDiv className="ql-detail-layer" role="presentation" onMouseDown={event => event.target === event.currentTarget && onClose()}><aside className="ql-detail" role="dialog" aria-modal="true" aria-labelledby="ql-detail-title"><DMDiv className="ql-detail-head"><DMDiv className="ql-detail-person"><LeadAvatar lead={lead} size="lg" /><DMDiv><DMText as="h2" id="ql-detail-title">{lead.parentName}</DMText><DMText>{lead.studentName} · Class {lead.joiningClass}</DMText></DMDiv></DMDiv><DMButton type="button" onClick={onClose} aria-label="Close lead details"><X /></DMButton></DMDiv><DMDiv className="ql-detail-actions"><a href={`tel:${lead.phone.replace(/\s+/g, '')}`}><Phone /> Call</a><a href={`https://wa.me/${lead.phone.replace(/[^\d]/g, '')}`} target="_blank" rel="noreferrer"><MessageCircle /> WhatsApp</a><DMButton type="button" onClick={onSms}><MessageCircle /> SMS</DMButton><a href={`mailto:${lead.email}`}><Mail /> Email</a></DMDiv><DMDiv className="ql-detail-scroll"><DMDiv className="ql-detail-facts"><DMDiv><small>Phone</small><a href={`tel:${lead.phone}`}>{lead.phone}</a></DMDiv><DMDiv><small>Email</small><a href={`mailto:${lead.email}`}>{lead.email}</a></DMDiv><DMDiv><small>Source</small><strong>{lead.source}</strong></DMDiv><DMDiv><small>Captured</small><strong>{lead.date}</strong></DMDiv></DMDiv><section><DMText as="h3">Lead status</DMText><DMDiv className="ql-detail-statuses">{STATUS_ORDER.map(status => <DMButton type="button" key={status} className={lead.status === status ? 'active' : ''} style={{ color: STATUS_META[status].color, borderColor: STATUS_META[status].color, background: lead.status === status ? STATUS_META[status].tint : '#fff' }} onClick={() => onStatusChange(status)}>{STATUS_META[status].label}</DMButton>)}</DMDiv></section><section><DMText as="h3">Activity</DMText><DMDiv className="ql-timeline"><span><Sparkles /></span><DMDiv><strong>Lead captured via {lead.source}</strong><small>{lead.date}</small></DMDiv></DMDiv>{lead.status !== 'NEW' && <DMDiv className="ql-timeline"><span><CheckCircle2 /></span><DMDiv><strong>{lead.status === 'CONVERTED' ? 'Admission confirmed' : lead.status === 'CONTACTED' ? 'Counsellor reached out to the parent' : 'Follow-up closed'}</strong><small>{STATUS_META[lead.status].label}</small></DMDiv></DMDiv>}</section><section><DMText as="h3">Notes</DMText><textarea value={note} onChange={event => setNote(event.target.value)} placeholder="Add a private note about this lead…" /></section></DMDiv><DMDiv className="ql-detail-footer"><DMButton type="button" className="ql-secondary" onClick={onClose}>Close</DMButton><DMButton type="button" className="ql-primary" onClick={onClose}>Save</DMButton></DMDiv></aside></DMDiv>;
}
